import React, {useState, useEffect} from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import './ArrCheckboxList.scss';
interface IndeterminateCheckboxProps {
  variant?: 'primary' | 'secondary';
  size?: 'xs' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  labelPlacement?: 'start' | 'end';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  shape?: boolean;
  ariaLabel?: string;
  childLabels: {label: string; value: boolean; disabled?: boolean}[];
  initialChecked?: boolean[];
  parentLabel?: string;
  onChange?: (checked: boolean[]) => void;
  showSelectAll?: boolean;
  tabIndex?: number;
  checkboxTestId?: string;
}

const ArrCheckboxList: React.FC<IndeterminateCheckboxProps> = ({
  parentLabel = 'Select All',
  childLabels, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialChecked,
  onChange,
  size = 'large',
  disabled = false,
  showSelectAll = true,
  checkboxTestId,
  ...props
}) => {
  const [checked, setChecked] = useState<boolean[]>(
    childLabels?.map((item) => item?.value)
  );

  useEffect(() => {
    onChange?.(checked);
  }, []);

  const allChecked = checked?.every(Boolean);
  const someChecked = checked?.some(Boolean);
  const isIndeterminate = someChecked && !allChecked;

  const updateCheckedState = (newChecked: boolean[]) => {
    setChecked(newChecked);
    onChange?.(newChecked);
  };

  const handleParentChange = (isChecked: boolean) => {
    const newChecked = checked.map(() => isChecked);
    updateCheckedState(newChecked);
  };

  const handleChildChange = (index: number) => (isChecked: boolean) => {
    const newChecked = [...checked];
    newChecked[index] = isChecked;
    updateCheckedState(newChecked);
  };

  useEffect(() => {
    setChecked(childLabels?.map((item) => item?.value));
  }, [childLabels]);

  return (
    <Box className="arr-checkbox-list">
      {showSelectAll && (
        <FormControlLabel
          data-testid={`arr-checkbox-list-${parentLabel}`}
          className="arr-checkbox-label"
          label={parentLabel}
          control={
            <ArrCheckbox
              aria-label={checkboxTestId ?? parentLabel}
              checked={someChecked}
              isIndeterminate={isIndeterminate}
              onChange={handleParentChange}
              size={size}
              disabled={disabled}
              {...props}
            />
          }
        />
      )}
      <Box className="arr-checkbox-children">
        {childLabels?.map((item, index) => (
          <Box
            className={`arr-checkbox-row ${checked && checked[index] ? 'selected' : ''}`}
            key={index}
          >
            <FormControlLabel
              className="arr-checkbox-label"
              label={item.label}
              control={
                <ArrCheckbox
                  checked={checked && checked[index]}
                  onChange={handleChildChange(index)}
                  size={size}
                  disabled={item?.disabled ?? disabled}
                  aria-label={`${item.label}-checkbox`}
                  {...props}
                />
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ArrCheckboxList;
